import { useEffect, useRef, useState } from "react";
import {
  CorrectionContextSchema,
  type CorrectionContext,
  ReportedVisitInputSchema,
  ReportedVisitPreviewInputSchema,
  ReportedVisitPreviewSchema,
  ReportedVisitResultSchema,
  UndoCorrectionInputSchema,
  type LocationVisit,
  type ReportedVisitPreviewInput,
} from "@smrti/types";

type Command = { visitId?: string; correctionId?: string; remove?: boolean; input: unknown };
const storageKey = (user: string) => `smrti:visit-time:${user}`;
function restore(user: string): Command | null {
  const saved = JSON.parse(sessionStorage.getItem(storageKey(user)) ?? "null");
  if (!saved) return null;
  if (saved.correctionId !== undefined) {
    if (
      typeof saved.visitId !== "string" ||
      !saved.visitId ||
      typeof saved.correctionId !== "string" ||
      !saved.correctionId ||
      (saved.remove !== undefined && typeof saved.remove !== "boolean")
    )
      throw Error("Invalid undo");
    return { ...saved, input: UndoCorrectionInputSchema.parse(saved.input) };
  }
  const input = ReportedVisitInputSchema.parse(saved.input);
  if (input.target.kind === "missing") {
    if (saved.visitId !== undefined) throw Error("Invalid missing stay");
    return { input };
  }
  if (input.target.visitId !== saved.visitId) throw Error("Invalid target");
  return { visitId: saved.visitId, input };
}
export function recoveredTimeVisit(user: string): { visitId: string | null } | null {
  try {
    const saved = restore(user);
    return saved ? { visitId: saved.visitId ?? null } : null;
  } catch {
    return { visitId: "unavailable-recovery" };
  }
}
function localInput(iso: string | null | undefined) {
  if (!iso) return "";
  const date = new Date(iso);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}
const describe = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "long",
      })
    : "Unknown";

export function VisitTimeEditor({
  userId,
  apiBase,
  visitId,
  visit,
  onClose,
  onChanged,
  onSignOut,
}: {
  userId: string;
  apiBase: string;
  visitId: string | null;
  visit?: LocationVisit;
  onClose: () => void;
  onChanged: () => void;
  onSignOut?: () => void;
}) {
  const original = useRef(visit).current;
  const [arrival, setArrival] = useState(localInput(visit?.arrival));
  const [departure, setDeparture] = useState(localInput(visit?.departure));
  const [accuracy, setAccuracy] = useState("");
  const [places, setPlaces] = useState<CorrectionContext["places"]>([]);
  const [query, setQuery] = useState("");
  const [placeId, setPlaceId] = useState("");
  const [savedVisitId, setSavedVisitId] = useState(visitId);
  const selectedPlace = places.find((p) => p.id === placeId);
  const matches = places.filter((p) =>
    `${p.name ?? ""} ${p.formattedAddress ?? ""}`.toLowerCase().includes(query.trim().toLowerCase())
  );
  const [preview, setPreview] = useState<{
    input: ReportedVisitPreviewInput;
    token: string;
    before: string;
    place?: string;
  } | null>(null);
  const [pending, setPending] = useState<Command | null>(null);
  const [undo, setUndo] = useState<{
    id: string;
    token: string;
    before: string;
    remove: boolean;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [message, setMessage] = useState("");
  const [settled, setSettled] = useState<"saved" | "undone" | null>(null);
  const active = useRef(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const confirmationHeading = useRef<HTMLHeadingElement>(null);
  const arrivalInput = useRef<HTMLInputElement>(null);
  const undoButton = useRef<HTMLButtonElement>(null);
  const placeSearch = useRef<HTMLInputElement>(null);
  const phase = undo
    ? "undo"
    : preview
      ? "preview"
      : settled
        ? "settled"
        : visitId === null && !selectedPlace
          ? "choose"
          : "form";
  const previousPhase = useRef(phase);
  useEffect(() => {
    if (phase === "undo" || phase === "preview") confirmationHeading.current?.focus();
    else if (phase === "choose" && previousPhase.current !== "choose") placeSearch.current?.focus();
    else if (previousPhase.current === "undo") undoButton.current?.focus();
    else if (previousPhase.current === "preview" || previousPhase.current === "choose")
      arrivalInput.current?.focus();
    previousPhase.current = phase;
  }, [phase]);

  async function identity() {
    const r = await fetch(`${apiBase}/api/me`, { credentials: "include", cache: "no-store" });
    const b = await r.json();
    if (!r.ok || !b.ok || b.data?.userId !== userId || !active.current)
      throw Error("Sign in to the original account before continuing.");
  }
  async function loadPlaces() {
    setBusy(true);
    try {
      await identity();
      const response = await fetch(`${apiBase}/api/location/places`, { credentials: "include" });
      const body = await response.json();
      if (!active.current) return;
      if (!response.ok || !body.ok) throw Error("Could not load saved places.");
      setPlaces(CorrectionContextSchema.shape.places.parse(body.data));
    } catch {
      if (active.current) setMessage("Could not load saved places. Reload to try again.");
    } finally {
      if (active.current) setBusy(false);
    }
  }
  useEffect(() => {
    active.current = true;
    dialog.current?.showModal?.();
    heading.current?.focus();
    try {
      const saved = restore(userId);
      setPending(saved);
      if (saved)
        setMessage(
          "A timing change is unconfirmed. Retry the saved request unchanged; keep this tab open."
        );
      else if (
        (visitId !== null && !visit) ||
        sessionStorage.getItem(`smrti:visit-place:${userId}`) ||
        sessionStorage.getItem(`smrti:place-confirmation:${userId}`)
      ) {
        setBlocked(true);
        setMessage("Resolve existing changes and reopen this visit before editing its times.");
      } else if (visitId === null) void loadPlaces();
    } catch {
      setBlocked(true);
      setMessage("Saved change recovery is unavailable. No change will be sent.");
    }
    const sheet = dialog.current;
    return () => {
      active.current = false;
      sheet?.close?.();
    };
    // Caller keys this sheet by account and visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function review() {
    setBusy(true);
    setMessage("");
    try {
      // Retain the original instant when unchanged, including its DST offset and seconds.
      const instant = (value: string, original?: string | null) => {
        if (original && localInput(original) === value) return original;
        const parsed = new Date(value);
        if (
          !value ||
          !Number.isFinite(parsed.getTime()) ||
          localInput(parsed.toISOString()) !== value
        )
          throw Error(
            "Enter valid local dates and times. A daylight-saving clock change may make this time unavailable."
          );
        // Do not silently pick one of two repeated local times.
        for (const days of [-2, -1, 1, 2]) {
          const offset = new Date(parsed.getTime() + days * 86400000).getTimezoneOffset();
          const difference = offset - parsed.getTimezoneOffset();
          if (
            difference !== 0 &&
            localInput(new Date(parsed.getTime() + difference * 60000).toISOString()) === value
          )
            throw Error(
              "This local time occurs twice during a clock change. Keep the recorded time or choose an unambiguous time."
            );
        }
        return parsed.toISOString();
      };
      const parsed = ReportedVisitPreviewInputSchema.safeParse({
        target:
          visitId === null
            ? { kind: "missing", placeId: selectedPlace?.id ?? "" }
            : { kind: "visit", visitId },
        arrival: instant(arrival, original?.arrival),
        departure: instant(departure, original?.departure),
        accuracy,
      });
      if (!parsed.success)
        throw Error(
          "Enter both boundaries, with departure after arrival, and choose exact or approximate."
        );
      if (Date.parse(parsed.data.departure) > Date.now())
        throw Error("Only completed stays can be reported. Departure cannot be in the future.");
      await identity();
      const r = await fetch(`${apiBase}/api/location/reported-visits/preview`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(parsed.data),
      });
      const b = await r.json();
      if (!active.current) return;
      if (!r.ok || !b.ok) throw Error(b.error ?? "Could not preview. Try again.");
      const result = ReportedVisitPreviewSchema.safeParse(b.data);
      if (
        !result.success ||
        (visitId !== null ? !result.data.current : result.data.current !== null) ||
        (parsed.data.target.kind === "missing" &&
          result.data.savedPlace?.id !== parsed.data.target.placeId)
      )
        throw Error("Could not read preview. Try again.");
      const { contextToken, current, savedPlace, conflicts } = result.data;
      if (conflicts.length)
        throw Error(
          "These times overlap another visit. Adjust your times; other visits will not be moved automatically."
        );
      setPreview({
        input: parsed.data,
        token: contextToken,
        before: current
          ? `${describe(current.arrival)} — ${describe(current.departure)}`
          : "No recorded stay",
        place:
          parsed.data.target.kind === "missing" && savedPlace
            ? `${savedPlace.name ?? "Unnamed saved place"} · ${savedPlace.formattedAddress ?? ""} · ${savedPlace.latitude.toFixed(4)}, ${savedPlace.longitude.toFixed(4)}`
            : undefined,
      });
    } catch (e) {
      if (active.current) setMessage(e instanceof Error ? e.message : "Could not preview.");
    } finally {
      if (active.current) setBusy(false);
    }
  }
  async function reviewUndo() {
    if (!savedVisitId) return;
    setBusy(true);
    setMessage("");
    try {
      await identity();
      const historyResponse = await fetch(
        `${apiBase}/api/location/visits/${encodeURIComponent(savedVisitId)}/time-history`,
        { credentials: "include" }
      );
      const history = await historyResponse.json();
      if (!active.current) return;
      const latest = history.data?.[0];
      if (
        !historyResponse.ok ||
        !history.ok ||
        typeof latest?.id !== "string" ||
        latest.visitId !== savedVisitId ||
        latest.undoOf ||
        !latest.after
      )
        throw Error("No previous timing change is available to undo.");
      const r = await fetch(
        `${apiBase}/api/location/reported-visit-corrections/${encodeURIComponent(latest.id)}/undo-context`,
        { credentials: "include" }
      );
      const b = await r.json();
      if (!active.current) return;
      if (
        !r.ok ||
        !b.ok ||
        !b.data?.canUndo ||
        b.data.visitId !== savedVisitId ||
        typeof b.data.contextToken !== "string"
      )
        throw Error(
          "Undo is unavailable: the visit changed, was already undone, or restoring it would overlap another visit."
        );
      setUndo({
        id: latest.id,
        token: b.data.contextToken,
        remove: latest.before === null,
        before: latest.before
          ? `${describe(latest.before.arrival)} — ${describe(latest.before.departure)} · ${latest.before.accuracy ?? "Not user-confirmed"}`
          : "Remove this reported stay. The saved place and its other visits remain.",
      });
    } catch (e) {
      if (active.current) setMessage(e instanceof Error ? e.message : "Could not check undo.");
    } finally {
      if (active.current) setBusy(false);
    }
  }
  async function send(command: Command) {
    setBusy(true);
    setMessage("");
    try {
      command = {
        ...command,
        input: command.correctionId
          ? UndoCorrectionInputSchema.parse(command.input)
          : ReportedVisitInputSchema.parse(command.input),
      };
      await identity();
      const saved = restore(userId);
      if (saved && JSON.stringify(saved) !== JSON.stringify(command))
        throw Error("Resolve saved request first");
      sessionStorage.setItem(storageKey(userId), JSON.stringify(command));
      setPending(command);
      const path = command.correctionId
        ? `reported-visit-corrections/${encodeURIComponent(command.correctionId)}/undo`
        : "reported-visits";
      const r = await fetch(`${apiBase}/api/location/${path}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(command.input),
      });
      const b = await r.json();
      if (!active.current) return;
      await identity();
      if (
        (r.status === 409 ||
          (!command.correctionId &&
            r.status === 404 &&
            (b.error === "Visit not found" || b.error === "Active saved place not found")) ||
          (!saved && [400, 401, 403, 404].includes(r.status))) &&
        b.ok === false &&
        typeof b.error === "string"
      ) {
        sessionStorage.removeItem(storageKey(userId));
        setPending(null);
        setPreview(null);
        setUndo(null);
        setMessage(
          "Change rejected. The visit or its overlaps may have changed. Review again before saving."
        );
        onChanged();
        if (visitId === null) await loadPlaces();
        return;
      }
      if (!r.ok || !b.ok) throw Error("Unconfirmed");
      const result = ReportedVisitResultSchema.parse(b.data);
      if (
        !result.visitId ||
        (command.visitId && result.visitId !== command.visitId) ||
        result.removed !== (command.remove ?? false)
      )
        throw Error("Unexpected result");
      sessionStorage.removeItem(storageKey(userId));
      setPending(null);
      setPreview(null);
      setUndo(null);
      setSavedVisitId(result.visitId);
      setSettled(command.correctionId ? "undone" : "saved");
      setMessage(
        command.remove
          ? "Reported stay removed. Saved place unchanged."
          : command.correctionId
            ? "Timing change undone. Place unchanged."
            : visitId === null
              ? "Reported stay saved at the selected place. Inside/nearby certainty unchanged."
              : "Reported times saved. Place and inside/nearby certainty unchanged."
      );
      onChanged();
    } catch {
      if (active.current)
        setMessage(
          "Could not confirm the change. Check your session and retry the same saved request. Keep this tab open while pending."
        );
    } finally {
      if (active.current) setBusy(false);
    }
  }
  return (
    <dialog
      ref={dialog}
      className="place-sheet"
      aria-label={visitId === null ? "Add missing stay" : "Edit visit times"}
      onCancel={(e) => {
        e.preventDefault();
        if (!busy && !pending) onClose();
      }}
    >
      <section className="max-w-2xl mx-auto space-y-4" aria-label="Change visit times">
        <button className="sheet-close" disabled={busy || !!pending} onClick={onClose}>
          Close <span aria-hidden="true">×</span>
        </button>
        <p className="text-xs text-stone-600">
          Closing discards unsaved choices, not saved changes.
        </p>
        <h2 ref={heading} tabIndex={-1} className="text-2xl font-semibold">
          {visitId === null ? "Add a missing stay" : "When were you here?"}
        </h2>
        {visitId !== null && <p>{original?.placeName ?? "This visit"}</p>}
        <p className="text-sm text-stone-600">
          {visitId === null
            ? "Choose a saved place and report both boundaries for a completed stay. This does not confirm inside versus nearby."
            : "Report both boundaries for this completed stay. Unknown times can remain unknown: close without saving. Place and inside/nearby certainty do not change."}
        </p>
        {message && (
          <p role="status" className="rounded border border-stone-300 p-3">
            {message}
          </p>
        )}
        {pending ? (
          <button
            disabled={busy}
            className="rounded bg-teal-800 text-white px-4"
            onClick={() => void send(pending)}
          >
            Retry same timing change
          </button>
        ) : (
          !blocked && (
            <>
              {undo ? (
                <div className="rounded border border-stone-300 p-4 space-y-3">
                  <h3 ref={confirmationHeading} tabIndex={-1}>
                    {undo.remove ? "Remove this reported stay?" : "Restore the previous times?"}
                  </h3>
                  <p>{undo.before}</p>
                  {!undo.remove && (
                    <p>
                      This restores the earlier timing and accuracy, not the place. It may restore
                      an unknown departure.
                    </p>
                  )}
                  <button
                    disabled={busy}
                    className="mr-4 rounded bg-teal-800 text-white px-4"
                    onClick={() =>
                      void send({
                        visitId: savedVisitId!,
                        correctionId: undo.id,
                        remove: undo.remove,
                        input: { operationId: crypto.randomUUID(), contextToken: undo.token },
                      })
                    }
                  >
                    {undo.remove ? "Confirm removal" : "Confirm timing undo"}
                  </button>
                  <button disabled={busy} onClick={() => setUndo(null)}>
                    Cancel undo
                  </button>
                </div>
              ) : preview ? (
                <div className="rounded border border-stone-300 p-4 space-y-3">
                  <h3 ref={confirmationHeading} tabIndex={-1} className="font-semibold">
                    Report {preview.input.accuracy} times?
                  </h3>
                  {preview.place && <p>{preview.place}</p>}
                  <p className="text-sm">Previously: {preview.before}</p>
                  <p>Arrival: {describe(preview.input.arrival)}</p>
                  <p>Departure: {describe(preview.input.departure)}</p>
                  {preview.place && (
                    <p className="text-sm">Saved-place coordinates are not sensor measurements.</p>
                  )}
                  <p className="text-sm">
                    Both boundaries will be reported as {preview.input.accuracy}. Other visits are
                    not edited.
                  </p>
                  <button
                    disabled={busy}
                    className="mr-4 rounded bg-teal-800 text-white px-4"
                    onClick={() =>
                      void send({
                        ...(visitId === null ? {} : { visitId }),
                        input: {
                          ...preview.input,
                          contextToken: preview.token,
                          operationId: crypto.randomUUID(),
                        },
                      })
                    }
                  >
                    {visitId === null ? "Save reported stay" : "Save reported times"}
                  </button>
                  <button disabled={busy} onClick={() => setPreview(null)}>
                    Back to times
                  </button>
                </div>
              ) : settled ? (
                <>
                  <p>
                    Close to return to your day. Reopen the visit to make another timing change.
                  </p>
                  {settled === "saved" && (
                    <button
                      ref={undoButton}
                      disabled={busy}
                      className="underline"
                      onClick={() => void reviewUndo()}
                    >
                      Undo previous timing change
                    </button>
                  )}
                </>
              ) : (
                <>
                  {visitId === null &&
                    (selectedPlace ? (
                      <div className="rounded border border-teal-200 bg-teal-50 p-3">
                        <p>
                          Selected place:{" "}
                          <strong>{selectedPlace.name ?? "Unnamed saved place"}</strong>
                        </p>
                        <p className="text-sm">{selectedPlace.formattedAddress}</p>
                        <button
                          disabled={busy}
                          className="underline"
                          onClick={() => setPlaceId("")}
                        >
                          Change selected place
                        </button>
                      </div>
                    ) : (
                      <>
                        <label className="block">
                          Search saved places
                          <input
                            ref={placeSearch}
                            type="search"
                            className="block w-full rounded border p-2"
                            value={query}
                            disabled={busy}
                            onChange={(e) => {
                              setQuery(e.target.value);
                              setPlaceId("");
                            }}
                          />
                        </label>
                        <button
                          disabled={busy}
                          className="underline"
                          onClick={() => void loadPlaces()}
                        >
                          Reload saved places
                        </button>
                        <fieldset disabled={busy} className="space-y-2">
                          <legend>Where was the missing stay?</legend>
                          {matches.map((p) => (
                            <label key={p.id} className="flex gap-3 border rounded p-3">
                              <input
                                type="radio"
                                name="missing-place"
                                checked={placeId === p.id}
                                onChange={() => setPlaceId(p.id)}
                              />
                              <span>
                                {p.name ?? "Unnamed saved place"}
                                <span className="block text-sm text-stone-600">
                                  {p.formattedAddress} · {p.latitude.toFixed(4)},{" "}
                                  {p.longitude.toFixed(4)}
                                </span>
                              </span>
                            </label>
                          ))}
                        </fieldset>
                        {!matches.length && (
                          <p>No matching saved places. This search does not discover new places.</p>
                        )}
                      </>
                    ))}
                  <p className="text-sm">
                    Times in {Intl.DateTimeFormat().resolvedOptions().timeZone}.
                    {visitId !== null &&
                      " Recorded estimates are starting points, not confirmed times."}
                  </p>
                  <label className="block">
                    Arrival
                    <input
                      ref={arrivalInput}
                      className="block w-full rounded border p-2"
                      type="datetime-local"
                      value={arrival}
                      disabled={busy}
                      onChange={(e) => setArrival(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    Departure
                    <input
                      className="block w-full rounded border p-2"
                      type="datetime-local"
                      value={departure}
                      disabled={busy}
                      onChange={(e) => setDeparture(e.target.value)}
                    />
                  </label>
                  <fieldset disabled={busy} className="space-y-2">
                    <legend>How well do you remember both times?</legend>
                    {(["approximate", "exact"] as const).map((value) => (
                      <label key={value} className="flex gap-3 border rounded p-3">
                        <input
                          type="radio"
                          name="time-accuracy"
                          value={value}
                          checked={accuracy === value}
                          onChange={() => setAccuracy(value)}
                        />
                        {value === "exact"
                          ? "Exact — I know both times"
                          : "Approximate — my best estimate"}
                      </label>
                    ))}
                  </fieldset>
                  <button
                    disabled={
                      busy ||
                      !accuracy ||
                      !arrival ||
                      !departure ||
                      (visitId === null && !selectedPlace)
                    }
                    className="rounded bg-teal-800 text-white px-4"
                    onClick={() => void review()}
                  >
                    Review times
                  </button>
                  {savedVisitId && (
                    <button
                      ref={undoButton}
                      disabled={busy}
                      className="block underline"
                      onClick={() => void reviewUndo()}
                    >
                      Undo previous timing change
                    </button>
                  )}
                </>
              )}
            </>
          )
        )}
        {onSignOut && (busy || blocked || pending) && (
          <button className="underline" onClick={onSignOut}>
            Sign out (keep saved change)
          </button>
        )}
      </section>
    </dialog>
  );
}
